import { useState, useEffect } from "react";
import { useElemContext } from "../Contexts/ElemContext";
import { fetchGroup } from "../services/groupService";
import {
  createExaminer,
  deleteExaminer,
  patchExaminer,
} from "../services/examinerService";
import {
  deleteCredentials,
  generateCredentials,
} from "../services/examinerCredentialService";
import { useFeedback } from "../Contexts/FeedbackContext";
import {
  ERROR_COPY,
  ERROR_CREATE,
  ERROR_DELETE,
  ERROR_DELETE_IDS,
  ERROR_NEW_IDS,
  ERROR_UPDATE,
  SUCCESS_COPY,
  SUCCESS_DELETE,
  SUCCESS_DELETE_IDS,
  SUCCESS_NEW_IDS,
  SUCCESS_UPDATE,
} from "../constants/feedbacks";
import { PROMPT_DELETE_EXAMINER } from "../constants/prompts";
import { TOKEN_KEY } from "../constants/storageKey";

export const useExaminer = () => {
  const token = localStorage.getItem(TOKEN_KEY);

  const { examiner, setExaminer, groups, setGroups, setExaminers } =
    useElemContext();
  const { showFeedback } = useFeedback();
  const [isDisabled, setIsDisabled] = useState(true);
  const [formData, setFormData] = useState({
    name: "",
    surname: "",
    username: "",
    password: "",
  });

  useEffect(() => {
    if (Object.keys(groups).length === 0) {
      fetchGroup()
        .then((response) => {
          setGroups(response.data);
        })
        .catch((error) => {
          console.error("Erreur de réseau:", error.message);
        });
    }
  }, [groups, setGroups, token]);

  useEffect(() => {
    setIsDisabled(true);
    if (examiner && examiner !== -1) {
      setFormData({
        name: examiner.name,
        surname: examiner.surname,
        username: examiner.user.username,
        password: examiner.user.password ? "**********" : "",
      });
    } else if (examiner && examiner === -1) {
      setFormData({
        name: "",
        surname: "",
        username: "",
        password: "",
      });
      setIsDisabled(false);
    }
  }, [examiner]);

  useEffect(() => {
    setExaminer(null);
  }, [setExaminer]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSettingsToggle = () => setIsDisabled(!isDisabled);

  const onSubmit = () => {
    if (!formData.name || !formData.surname) return;
    const body = {
      name: formData.name,
      surname: formData.surname,
    };
    if (examiner !== -1) {
      patchExaminer(examiner.id, body)
        .then((res) => {
          setExaminer(res.data);
          showFeedback(SUCCESS_UPDATE, true);
        })
        .catch((error) => {
          showFeedback(ERROR_UPDATE, false);
          console.error(error);
        });
    } else {
      createExaminer(body)
        .then((response) => {
          setExaminer(response.data);
          const new_exa = {
            id: response.data.id,
            name: response.data.name + " " + response.data.surname,
          };
          setExaminers((prevExaminers) => {
            return { ...prevExaminers, new_exa };
          });
          setIsDisabled(false);
        })
        .catch((error) => {
          showFeedback(ERROR_CREATE, false);
          console.error(error);
        });
    }
  };

  const handleCredentials = (action) => {
    if (action === "generate") {
      generateCredentials(examiner.id)
        .then((response) => {
          setFormData((prev) => ({
            ...prev,
            username: response.data.username,
            password: response.data.password,
          }));
          showFeedback(SUCCESS_NEW_IDS, true);
        })
        .catch(() => {
          showFeedback(ERROR_NEW_IDS, false);
        });
    } else if (action === "delete") {
      deleteCredentials(examiner.id)
        .then(() => {
          setFormData((prev) => ({
            ...prev,
            username: "",
            password: "",
          }));
          showFeedback(SUCCESS_DELETE_IDS, true);
        })
        .catch(() => {
          showFeedback(ERROR_DELETE_IDS, false);
        });
    }
  };

  const handleDeleteExaminer = () => {
    if (window.confirm(PROMPT_DELETE_EXAMINER)) {
      deleteExaminer(examiner.id)
        .then(() => {
          setExaminers((prev) => {
            const updatedPrev = Object.values(prev).filter((elem) => {
              return elem.id !== examiner.id;
            });
            return updatedPrev;
          });
          setExaminer(null);
          showFeedback(SUCCESS_DELETE, true);
        })
        .catch(() => {
          showFeedback(ERROR_DELETE, false);
        });
    }
  };

  function handleCopy(inputValue) {
    navigator.clipboard
      .writeText(inputValue)
      .then(() => {
        showFeedback(SUCCESS_COPY, true);
      })
      .catch(() => showFeedback(ERROR_COPY, false));
  }
  return {
    examiner,
    formData,
    isDisabled,
    handleChange,
    onSubmit,
    handleSettingsToggle,
    handleCopy,
    handleCredentials,
    handleDeleteExaminer,
    groups,
  };
};
